import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";

const initialState = {
	list: [],
	loading: false,
	error: null,
};

const extractMessage = (payload, fallback) => {
	const message =
		payload?.message ??
		payload?.Message ??
		payload?.statusMessage ??
		payload?.StatusMessage ??
		payload?.Status ??
		payload?.status;
	return typeof message === "string" && message.trim() ? message : fallback;
};

export const loadProjectTypes = createAsyncThunk(
	"projectType/loadAll",
	async (_, { rejectWithValue }) => {
		try {
			const { data } = await axios.get("projects/ProjectTypeGetbyCompanyId");
			return Array.isArray(data) ? data : [];
		} catch (error) {
			return rejectWithValue(
				error?.response?.data ?? {
					message: error?.message || "Unable to load project types",
				}
			);
		}
	}
);

export const saveProjectType = createAsyncThunk(
	"projectType/saveSingle",
	async (payload, { rejectWithValue }) => {
		const { ProjectTypeId } = payload || {};
		const normalizedPayload = {
			ProjectTypeId: ProjectTypeId ?? 0,
			ProjectTypeName: payload?.ProjectTypeName ?? "",
			ProjectTypeDescription: payload?.ProjectTypeDescription ?? null,
			DataStatus: payload?.DataStatus ?? 1,
		};
		try {
			const { data } = await axios.post("projects/ProjectTypeSetup", normalizedPayload);
			const message = extractMessage(
				data,
				ProjectTypeId && ProjectTypeId !== 0
					? "Project type updated successfully"
					: "Project type added successfully"
			);
			toast.success(message);
			return data;
		} catch (error) {
			const responseData = error?.response?.data;
			toast.error(
				extractMessage(
					responseData,
					"Failed to save project type"
				)
			);
			return rejectWithValue(
				responseData ?? {
					message: error?.message || "Unable to save project type",
				}
			);
		}
	}
);

export const deleteProjectType = createAsyncThunk(
	"projectType/deleteSingle",
	async (payload, { rejectWithValue }) => {
		const { ProjectTypeId } = payload || {};
		if (!ProjectTypeId) {
			return rejectWithValue({ message: "Invalid project type id" });
		}
		try {
			const { data } = await axios.post("projects/ProjectTypeSetup", {
				ProjectTypeId,
				DataStatus: 2,
			});
			const message = extractMessage(data, "Project type deleted successfully");
			toast.success(message);
			return {
				data,
				ProjectTypeId,
			};
		} catch (error) {
			const responseData = error?.response?.data;
			toast.error(
				extractMessage(
					responseData,
					"Failed to delete project type"
				)
			);
			return rejectWithValue(
				responseData ?? {
					message: error?.message || "Unable to delete project type",
				}
			);
		}
	}
);

const projectTypeSlice = createSlice({
	name: "projectType",
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(loadProjectTypes.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(loadProjectTypes.fulfilled, (state, action) => {
				state.loading = false;
				state.list = action.payload ?? [];
			})
			.addCase(loadProjectTypes.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload?.message || "Unable to load project types";
			})
			.addCase(saveProjectType.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(saveProjectType.fulfilled, (state) => {
				state.loading = false;
			})
			.addCase(saveProjectType.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload?.message || "Unable to save project type";
			})
			.addCase(deleteProjectType.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(deleteProjectType.fulfilled, (state, action) => {
				state.loading = false;
				const deletedId = action.payload?.ProjectTypeId;
				if (deletedId) {
					state.list = state.list.filter(
						(item) => Number(item.ProjectTypeId) !== Number(deletedId)
					);
				}
			})
			.addCase(deleteProjectType.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload?.message || "Unable to delete project type";
			});
	},
});

export default projectTypeSlice.reducer;

